<template>
  <Card class="integration-webtop-units">
    <template #header>
      <slot name="header" />
    </template>
    <template #default>
      <Loader :value="promises.fetch" theme="overlay">
        <table>
          <tr>
            <th>Enhet i Stream</th>
            <th>Enhet i Kvass</th>
            <th>Status</th>
          </tr>
          <tbody>
            <tr v-for="item in webtop" :key="item.value">
              <td>
                <strong v-if="item.label" class="integration-webtop-units__public">{{ item.label }}</strong>
                <span v-if="item.sublabel" class="integration-webtop-units__internal">{{ item.sublabel }}</span>
              </td>
              <td>
                <select :value="value.map[item.value]" :disabled="Boolean(progress)" @input="$ev => setMap(item, $ev)">
                  <option :value="null">Opprett ny enhet</option>
                  <option :value="false">Hopp over</option>
                  <option
                    v-for="kitem in getKvassUnits(item.value)"
                    :key="kitem.value"
                    :value="kitem.value"
                    :disabled="isUsed(kitem.value, item.value)"
                  >
                    {{ kitem.label }}
                  </option>
                </select>
              </td>
              <td>
                <span
                  :class="[
                    'integration-webtop-units__status',
                    `integration-webtop-units__status--${getStatus(item.value).key}`,
                  ]"
                >
                  <Icon :icon="getStatus(item.value).icon" />
                  {{ getStatus(item.value).label }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3">
                <div class="integration-webtop-units__counters">
                  <span v-for="status in statusCounts" :key="status.key">{{ status.label }}: {{ status.count }}</span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </Loader>
    </template>
    <template #footer>
      <Alert v-if="hasDuplicateSelection" theme="error"> Du har brukt en enhet i Kvass flere ganger. </Alert>
      <Alert v-if="progress">{{ progress }}</Alert>
      <Alert v-if="error" theme="error" class="integration-webtop-units__error">{{ error }}</Alert>
      <ButtonComponent v-if="error && !webtop.length" label="Tilbake" @click="$emit('navigate', 'activate')" />

      <ButtonComponent v-if="value.steps.length" label="Gå tilbake" @click="() => $emit('navigate', 'step')" />

      <ButtonComponent
        :label="error ? 'Prøv igjen' : 'Fullfør'"
        :icon="['fal', 'arrow-to-right']"
        theme="primary"
        :disabled="isDisabled"
        @click="error ? retry() : submit()"
        :promise="promises.submit"
      />
    </template>
  </Card>
</template>

<script>
import Validator from 'validatorjs'
import { ValidatorSettings } from './activate'
import { request, stream } from '@/api/request'

import Card from '@kvass/card'
import { ButtonComponent } from 'vue-elder-button'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import Icon from '@kvass/vue2-icon'
import { AlertComponent as Alert } from 'vue-elder-alert'

const Status = {
  synced: { label: 'Oppkoblet', icon: 'fa-pro-solid:check' },
  pending: { label: 'Opprettes', icon: 'fa-pro-solid:plus' },
  skipped: { label: 'Frakoblet', icon: 'fa-pro-solid:link-slash' },
}

const noDupes = arr => {
  return arr.every(x => arr.filter(u => u === x).length <= 1)
}

function convertTarget(val) {
  switch (val) {
    case 'false':
      return false
    case '':
      return null
    default:
      return val
  }
}

export default {
  props: {
    value: Object,
  },
  data() {
    return {
      promises: {
        fetch: null,
        submit: null,
      },
      webtop: [],
      kvass: [],
      error: null,
      progress: null,
    }
  },
  computed: {
    isDisabled() {
      if (this.progress) return true
      if (this.error && !this.webtop.length) return false
      return this.hasDuplicateSelection
    },
    hasDuplicateSelection() {
      return !noDupes(this.usedUnits)
    },
    usedUnits() {
      return Object.values(this.value.map || {}).filter(Boolean)
    },
    statusCounts() {
      return Object.entries(Status).map(([key, status]) => ({
        ...status,
        key,
        count: this.webtop.filter(({ value }) => this.getStatus(value).key === key).length,
      }))
    },
  },
  methods: {
    getStatus(id) {
      const value = (this.value.map || {})[id]
      if (value === false) return { key: 'skipped', ...Status.skipped }
      if (value) return { key: 'synced', ...Status.synced }
      return { key: 'pending', ...Status.pending }
    },
    isUsed(kvassId, id) {
      return this.value.map[id] !== kvassId && this.usedUnits.includes(kvassId)
    },
    getKvassUnits(id) {
      return [...this.kvass].sort((a, b) => Number(this.isUsed(a.value, id)) - Number(this.isUsed(b.value, id)))
    },
    retry() {
      if (!this.webtop.length) return this.fetch()
      return this.submit()
    },
    fetch() {
      this.error = null

      this.promises.fetch = request(`https://${this.value.account}/api/integration/${this.value.id}/callbacks/map`, {
        headers: {
          Authorization: `Bearer ${this.value.token}`,
        },
      })
        .then(res => res.json())
        .then(({ map, kvass, webtop, steps }) => {
          if (steps) {
            this.$emit('input', { ...this.value, steps: steps })
            return this.$emit('navigate', 'step')
          }

          this.$emit('input', { ...this.value, map })

          this.kvass = kvass
          this.webtop = webtop
        })
        .catch(err => (this.error = err))
    },
    submit() {
      if (this.isDisabled) return

      this.error = null
      this.progress = 'Lagrer oppkobling…'

      this.promises.submit = stream(
        `https://${this.value.account}/api/integration/${this.value.id}/callbacks/map`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.value.token}`,
          },
          body: JSON.stringify(this.value.map),
        },
        message => (this.progress = message),
      )
        .then(() => this.$emit('navigate', 'completed'))
        .catch(err => {
          this.error = err
          throw err
        })
        .finally(() => (this.progress = null))
    },
    setMap(item, event) {
      let value = convertTarget(event.target.value)
      this.$emit('input', { ...this.value, map: { ...this.value.map, [item.value]: value } })
    },
  },
  mounted() {
    if (new Validator(this.value, ValidatorSettings.rules).passes() === false) return this.$emit('navigate', 'activate')

    this.fetch()
  },
  components: {
    Loader,
    Icon,
    ButtonComponent,
    Card,
    Alert,
  },
}
</script>

<style lang="scss">
.integration-webtop-units {
  table {
    border-collapse: collapse;
    width: 100%;
    th,
    td {
      padding: 0.75rem;
      text-align: left;
    }

    tbody tr:nth-child(odd) {
      background-color: hsla(var(--border-color-h), var(--border-color-s), var(--border-color-l), 0.3);
    }

    tfoot td {
      position: sticky;
      bottom: 0;
      padding: 1rem 1.5rem;
      background-color: white;
      border-top: 1px solid #eaeaea;
    }
  }

  &__public,
  &__internal {
    display: block;
  }

  &__internal {
    font-size: 0.8125rem;
    opacity: 0.7;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4em 0.7em;
    border-radius: 30px;
    font-size: 1rem;
    line-height: 1;
    letter-spacing: 0.02rem;
    white-space: nowrap;

    &--synced {
      background-color: #cdf8de;
      color: #075718;
    }

    &--pending {
      background-color: #fff5d6;
      color: #80570c;
    }

    &--skipped {
      background-color: #f0f0f0;
      color: #000000;
      opacity: 0.6;
    }
  }

  &__counters {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    font-size: 1rem;
  }

  .alert p {
    margin-top: 0;
  }

  &__error {
    white-space: pre-line;
  }
}
</style>
